import { useCallback, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { DisconnectOutlined, EditOutlined } from "@ant-design/icons";
import { Button, InputNumber, Popover, Select, Tooltip } from "antd";
import { motionAPI } from "@blings/blings-player/editor-tools";
import { updateObjectValues } from "../../helpers/JsonSearch";
import { useMst } from "../../stores/main";
import {
  detectTextAnimationPhase,
  detectTextAnimationGrouping,
  getKeyframePropertyPath,
  TEXT_ANIMATION_PHASES,
  TEXT_ANIMATION_GROUPINGS,
  TREE_ROW_HEIGHT,
} from "./utils/keyframeHelpers";
import type { TimelinePropertyNode } from "./utils/layerHierarchy";
import { getLayerData } from "./utils/layerHierarchy";
import { getResolvedLayerTiming } from "./TimelineTrack";
import { applyCustomModsToLayerData } from "./utils/moddedLayerData";
import { buildDisconnectAnimationChange } from "./utils/propertyAnimationModHelpers";
import { getValueAtObjectPath } from "./utils/rootAnimationModHelpers";
import { useAnimationJsonEdit } from "./hooks/useAnimationJsonEdit";
import {
  TEXT_ANIMATION_PHASE_LABELS,
  buildTextAnimationSelectorTiming,
  getStaticNumberValue,
  getTextAnimationPhaseTiming,
  getTimelineMotionTarget,
} from "./utils/timelineLayerAnimationUtils";

function PropertyDiamondIcon(): JSX.Element {
  return (
    <svg
      width="8"
      height="8"
      viewBox="0 0 8 8"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        x="4"
        y="0.5"
        width="5"
        height="5"
        rx="0.5"
        transform="rotate(45 4 0.5)"
        fill="currentColor"
      />
    </svg>
  );
}

export const PropertyNodeLabel = observer(
  ({ node }: { node: TimelinePropertyNode }) => {
    const {
      playerStore: { jsonFile, currentFrame },
      modsStore,
    } = useMst();
    const { applyAnimationJsonEdit } = useAnimationJsonEdit();
    const { depth, propertyName, parentLayer, textAnimation } = node;
    const [isEditorOpen, setIsEditorOpen] = useState(false);
    const modsRevision = modsStore.trigger;
    const mods = useMemo(() => {
      void modsRevision;
      return modsStore.mods?.slice() ?? [];
    }, [modsRevision, modsStore.mods]);

    const resolvedLayerData = useMemo(() => {
      const layerData = getLayerData(jsonFile, parentLayer.layerPath);
      return applyCustomModsToLayerData(layerData, parentLayer, mods);
    }, [jsonFile, mods, parentLayer]);
    const layerTiming = useMemo(
      () => getResolvedLayerTiming(parentLayer, mods),
      [mods, parentLayer]
    );

    const handleRemoveAnimation = useCallback(() => {
      if (textAnimation) {
        applyAnimationJsonEdit((workingAnimation) => {
          const workingLayerData = getLayerData(
            workingAnimation,
            parentLayer.layerPath
          );
          const disconnectChange = buildDisconnectAnimationChange({
            keyframes: node.keyframes,
            propertyType: node.propertyType,
            textAnimation,
            resolvedLayerData: workingLayerData,
            layerTiming,
            currentFrame,
          });
          if (!disconnectChange) {
            return false;
          }

          updateObjectValues(
            workingLayerData as Record<string, unknown>,
            [disconnectChange.path],
            [disconnectChange.value as any]
          );

          if (disconnectChange.closeEditor) {
            setIsEditorOpen(false);
          }
          return true;
        });
        return;
      }

      const propertyPaths = Array.from(
        new Set(
          node.keyframes.map((keyframe) =>
            getKeyframePropertyPath(keyframe.path)
          )
        )
      );
      if (!propertyPaths.length) {
        return;
      }

      applyAnimationJsonEdit((workingAnimation) => {
        const workingLayerData = getLayerData(
          workingAnimation,
          parentLayer.layerPath
        );
        const disconnectChange = buildDisconnectAnimationChange({
          keyframes: node.keyframes,
          propertyType: node.propertyType,
          resolvedLayerData: workingLayerData,
          layerTiming,
          currentFrame,
        });
        if (!disconnectChange) {
          return false;
        }

        updateObjectValues(
          workingLayerData as Record<string, unknown>,
          [disconnectChange.path],
          [disconnectChange.value as any]
        );

        return true;
      });
    }, [
      applyAnimationJsonEdit,
      currentFrame,
      layerTiming,
      node.keyframes,
      node.propertyType,
      parentLayer,
      textAnimation,
    ]);

    const groupingValue = textAnimation?.groupingPath
      ? detectTextAnimationGrouping(
          getValueAtObjectPath(resolvedLayerData, textAnimation.groupingPath)
        ) ?? textAnimation.grouping
      : undefined;
    const amountValue = textAnimation?.amountPath
      ? getStaticNumberValue(
          getValueAtObjectPath(resolvedLayerData, textAnimation.amountPath)
        ) ?? textAnimation.amount
      : undefined;
    const phaseValue = textAnimation?.phasePath
      ? getValueAtObjectPath(resolvedLayerData, textAnimation.phasePath)
      : undefined;
    const phaseSelection =
      detectTextAnimationPhase(phaseValue) ??
      textAnimation?.phase ??
      (textAnimation?.phasePath ? "in" : undefined);

    return (
      <div
        className="timeline-tree-node timeline-tree-node--property"
        style={{ height: TREE_ROW_HEIGHT }}
      >
        <div
          className="timeline-tree-node__indent"
          style={{ width: depth * 12 }}
        />

        <div className="timeline-tree-node__toggle-spacer" />
        <div className="timeline-tree-node__keyframe-toggle-spacer" />

        <div className="timeline-tree-node__icon timeline-tree-node__icon--property">
          <PropertyDiamondIcon />
        </div>

        <span className="timeline-tree-node__name timeline-tree-node__name--property">
          {propertyName}
        </span>

        <div className="timeline-tree-node__actions">
          <Tooltip title="Disconnect animation" placement="right">
            <button
              type="button"
              className="timeline-tree-node__row-action timeline-tree-node__row-action--danger"
              onClick={(event) => {
                event.stopPropagation();
                handleRemoveAnimation();
              }}
              onMouseDown={(event) => event.stopPropagation()}
              aria-label="Disconnect animation"
            >
              <DisconnectOutlined
                className="timeline-tree-node__connection-icon"
                aria-hidden="true"
              />
            </button>
          </Tooltip>

          {textAnimation && (
            <Popover
              open={isEditorOpen}
              trigger="click"
              placement="rightTop"
              onOpenChange={setIsEditorOpen}
              content={
                <div
                  className="timeline-text-animation-editor"
                  onMouseDown={(event) => event.stopPropagation()}
                  onClick={(event) => event.stopPropagation()}
                >
                  <div className="timeline-text-animation-editor__title">
                    {textAnimation.displayName}
                  </div>
                  <div className="timeline-text-animation-editor__subtitle">
                    {textAnimation.source === "preset"
                      ? "Blings preset"
                      : "Custom text animation"}
                  </div>

                  {textAnimation.groupingPath && (
                    <label className="timeline-text-animation-editor__field">
                      <span>Grouping</span>
                      <Select
                        size="small"
                        value={groupingValue}
                        popupMatchSelectWidth={false}
                        popupClassName="timeline-text-animation-editor__select-popup"
                        options={TEXT_ANIMATION_GROUPINGS.map((grouping) => ({
                          value: grouping,
                          label:
                            grouping.charAt(0).toUpperCase() +
                            grouping.slice(1),
                        }))}
                        onChange={(nextGrouping) => {
                          applyAnimationJsonEdit((workingAnimation) => {
                            const applied = motionAPI.apply.updateTextAnimatorSelector(
                              workingAnimation as any,
                              getTimelineMotionTarget(parentLayer),
                              textAnimation.animatorIndex,
                              {
                                grouping: nextGrouping,
                              }
                            );

                            return applied.changes.length ? applied : false;
                          });
                        }}
                      />
                    </label>
                  )}

                  {textAnimation.phasePath && phaseSelection && (
                    <label className="timeline-text-animation-editor__field">
                      <span>Phase</span>
                      <Select
                        size="small"
                        value={phaseSelection}
                        popupMatchSelectWidth={false}
                        popupClassName="timeline-text-animation-editor__select-popup"
                        options={TEXT_ANIMATION_PHASES.map((phase) => ({
                          value: phase,
                          label: TEXT_ANIMATION_PHASE_LABELS[phase],
                        }))}
                        onChange={(nextPhase) => {
                          const nextTiming = buildTextAnimationSelectorTiming({
                            phase: nextPhase,
                            existingTiming: getTextAnimationPhaseTiming(
                              phaseValue
                            ),
                            layerTiming,
                          });
                          applyAnimationJsonEdit((workingAnimation) => {
                            const applied = motionAPI.apply.updateTextAnimatorSelector(
                              workingAnimation as any,
                              getTimelineMotionTarget(parentLayer),
                              textAnimation.animatorIndex,
                              {
                                phase: nextPhase,
                                timing: nextTiming,
                              }
                            );

                            return applied.changes.length ? applied : false;
                          });
                        }}
                      />
                    </label>
                  )}

                  {textAnimation.amountPath && amountValue != null && (
                    <label className="timeline-text-animation-editor__field">
                      <span>Amount</span>
                      <InputNumber
                        size="small"
                        value={amountValue}
                        onChange={(nextValue) => {
                          if (typeof nextValue !== "number") {
                            return;
                          }
                          applyAnimationJsonEdit((workingAnimation) => {
                            const applied = motionAPI.apply.updateTextAnimatorSelector(
                              workingAnimation as any,
                              getTimelineMotionTarget(parentLayer),
                              textAnimation.animatorIndex,
                              {
                                selectorAmount: nextValue,
                              }
                            );

                            return applied.changes.length ? applied : false;
                          });
                        }}
                      />
                    </label>
                  )}

                  {!textAnimation.groupingPath &&
                    !textAnimation.phasePath &&
                    (!textAnimation.amountPath || amountValue == null) && (
                      <div className="timeline-text-animation-editor__empty">
                        No row-level settings yet. Timing and easing are edited
                        on the keyframes.
                      </div>
                    )}

                  <div className="timeline-keyframe-editor__actions">
                    <Button size="small" onClick={() => setIsEditorOpen(false)}>
                      Close
                    </Button>
                  </div>
                </div>
              }
            >
              <button
                type="button"
                className={`timeline-tree-node__row-action ${
                  isEditorOpen ? "timeline-tree-node__row-action--visible" : ""
                }`}
                onClick={(event) => event.stopPropagation()}
                onMouseDown={(event) => event.stopPropagation()}
                title="Edit text animation"
              >
                <EditOutlined />
              </button>
            </Popover>
          )}
        </div>
      </div>
    );
  }
);
